import { useState, useEffect, useMemo } from "react";
import { View, Text, StyleSheet } from "react-native";
import { platformAlert } from "../utils/platformAlert";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";

import ImagePickerStrip from "../components/media/ImagePicker";
import { supabase } from "../lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigation, RouteProp } from "@react-navigation/native";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Button } from "../components/ui/Button";
import type { RootStackParamList } from "../types/navigation";
import { CATEGORIES } from "../lib/categories";
import { useToast } from "../contexts/ToastContext";
import { StripeVerificationModal } from "../components/ui/StripeVerificationModal";
import { useVendorProfile } from "../hooks/useVendorProfile";
import { LoadingState } from "../components/ui/LoadingState";
import { usePostHog } from "../hooks/usePostHog";
import { trackEvent } from "../services/posthog";
interface AddProductScreenProps {
  route?: RouteProp<RootStackParamList, "AddProduct">;
}

export default function AddProductScreen({ route }: AddProductScreenProps) {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const posthog = usePostHog();
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [stripeStatus, setStripeStatus] = useState<string | null>(null);
  const [priceFocused, setPriceFocused] = useState(false);
  const existingProduct = route?.params?.product;
  const isEditing = !!existingProduct;
  const { data: vendor, isLoading, refetch } = useVendorProfile();

  // Character limits for form fields
  const MAX_TITLE_LENGTH = 80;
  const MAX_DESCRIPTION_LENGTH = 500;
  const MAX_PRICE_LENGTH = 14; // Increased to accommodate commas (e.g., $999,999.99)
  const MAX_QUANTITY_LENGTH = 5;
  const MAX_SIZE_LENGTH = 100;
  const MAX_BRAND_LENGTH = 50;
  const MAX_COLOR_LENGTH = 100;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    images: [] as string[],
    status: "active",
    size: [] as string[],
    brand: "",
    color: [] as string[],
    condition: "New",
    position: 0,
    quantity: "1",
    category_main: "",
    category_subcategory: "",
    category_item: "",
    type: "casual_wear",
  });

  // vendorId state and effect removed; use vendor from useVendorProfile

  useEffect(() => {
    if (existingProduct) {
      // First, set form data with existing product (may have stale quantity)
      setFormData({
        title: existingProduct.title || "",
        description: existingProduct.description || "",
        price: existingProduct.price?.toString() || "",
        images: existingProduct.images || [],
        status: existingProduct.status || "active",
        size: existingProduct.size || [],
        brand: existingProduct.brand || "",
        color: existingProduct.color || [],
        condition: existingProduct.condition || "New",
        position: existingProduct.position || 0,
        quantity: existingProduct.quantity?.toString() || "1",
        category_main: existingProduct.category_main || "",
        category_subcategory: existingProduct.category_subcategory || "",
        category_item: existingProduct.category_item || "",
        type: existingProduct.type || "casual_wear",
      });

      // Then fetch the current quantity from database to ensure it's up-to-date
      const fetchCurrentQuantity = async () => {
        try {
          const { data, error } = await supabase
            .from("products")
            .select("quantity")
            .eq("id", existingProduct.id)
            .single();

          if (error) {
            console.error("[AddProductScreen] Error fetching current quantity:", error);
            return;
          }

          if (data) {
            const currentQuantity = data.quantity ?? 0;
            console.log(`[AddProductScreen] Fetched current quantity for product ${existingProduct.id}: ${currentQuantity}`);
            setFormData((prev) => ({
              ...prev,
              quantity: currentQuantity.toString(),
            }));
          }
        } catch (err) {
          console.error("[AddProductScreen] Error in fetchCurrentQuantity:", err);
        }
      };

      fetchCurrentQuantity();
    }
  }, [existingProduct]);

  // Helper to add thousand separators (commas)
  const addThousandSeparators = (numStr: string): string => {
    // Split by decimal point
    const parts = numStr.split(".");
    // Add commas to the integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  // Format price for display as $x,xxx.xx with thousand separators
  const formatPriceForDisplay = (value: string): string => {
    if (!value) return "";
    
    // While focused, show $ prefix with commas but preserve user's decimal input
    if (priceFocused) {
      return `$${addThousandSeparators(value)}`;
    }
    
    // When not focused, format with 2 decimal places and commas
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return `$${addThousandSeparators(num.toFixed(2))}`;
  };

  // Handle price input changes - strip $ and commas, store numeric value only
  const handlePriceChange = (text: string) => {
    // Remove $ symbol and commas, store only the numeric value
    const cleaned = text.replace(/[$,]/g, "");
    setFormData((prev) => ({ ...prev, price: cleaned }));
  };

  // Get main categories (only Men's Fashion, Women's Fashion, and Kids)
  const mainCategories = CATEGORIES.filter((category) =>
    ["mens-fashion", "womens-fashion", "kids"].includes(category.id)
  ).map((category) => ({
    label: category.name,
    value: category.id,
  }));

  // Get available subcategories based on selected main category
  const availableSubcategories = useMemo(() => {
    if (!formData.category_main) return [];
    const category = CATEGORIES.find((c) => c.id === formData.category_main);
    if (!category) return [];
    return category.subcategories.map((sub) => ({
      label: sub.name,
      value: sub.id,
    }));
  }, [formData.category_main]);

  // Get available items based on selected subcategory
  const availableItems = useMemo(() => {
    if (!formData.category_main || !formData.category_subcategory) return [];
    const category = CATEGORIES.find((c) => c.id === formData.category_main);
    if (!category) return [];
    const subcategory = category.subcategories.find(
      (sub) => sub.id === formData.category_subcategory
    );
    if (!subcategory || !subcategory.items) return [];
    return subcategory.items.map((item) => ({
      label: item,
      value: item,
    }));
  }, [formData.category_main, formData.category_subcategory]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Use the vendor from the hook
      if (!vendor) {
        throw new Error(
          "No vendor found for this user. Please set up your seller profile first."
        );
      }

      // Check Stripe account status before allowing product creation
      if (vendor.stripe_account_status !== "active") {
        setStripeStatus(vendor.stripe_account_status);
        setShowStripeModal(true);
        return;
      }

      // Comprehensive validation
      const validationErrors = [];

      // Image validation
      if (!formData.images || formData.images.length === 0) {
        validationErrors.push("At least one product image is required");
      }

      // Required field validation
      if (!formData.title.trim()) {
        validationErrors.push("Product title is required");
      }

      if (!formData.description.trim()) {
        validationErrors.push("Product description is required");
      }

      if (!formData.price.trim()) {
        validationErrors.push("Price is required");
      } else {
        const price = parseFloat(formData.price);
        if (isNaN(price) || price <= 0) {
          validationErrors.push("Price must be a valid positive number");
        }
      }

      if (!formData.quantity.trim()) {
        validationErrors.push("Quantity is required");
      } else {
        const quantity = parseInt(formData.quantity);
        if (isNaN(quantity) || quantity < 1) {
          validationErrors.push("Quantity must be at least 1");
        }
      }

      // Category validation
      if (!formData.category_main) {
        validationErrors.push("Main category is required");
      }

      if (!formData.category_subcategory) {
        validationErrors.push("Subcategory is required");
      }

      if (!formData.category_item) {
        validationErrors.push("Item type is required");
      }

      // Condition validation
      if (!formData.condition) {
        validationErrors.push("Condition is required");
      }

      // Brand validation
      if (!formData.brand.trim()) {
        validationErrors.push("Brand is required");
      }

      // Show validation errors
      if (validationErrors.length > 0) {
        platformAlert(
          "Missing Required Information",
          validationErrors.join("\n• "),
          [{ text: "OK" }]
        );
        return;
      }

      const productData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        images: formData.images,
        vendor_id: vendor.id, // Use vendor from hook
        status: "active", // Make sure this matches the filter in useMyProducts
        size: Array.isArray(formData.size) ? formData.size : [formData.size],
        brand: formData.brand,
        color: Array.isArray(formData.color)
          ? formData.color
          : [formData.color],
        condition: formData.condition,
        position: formData.position,
        quantity: parseInt(formData.quantity),
        category_main: formData.category_main,
        category_subcategory: formData.category_subcategory,
        category_item: formData.category_item,
        type: formData.type,
      };

      let error;
      if (isEditing) {
        const { error: updateError } = await supabase
          .from("products")
          .update(productData)
          .eq("id", existingProduct.id);
        error = updateError;
      } else {
        console.log(
          "Creating new product with data:",
          JSON.stringify(productData)
        );
        const { data: insertedProduct, error: insertError } = await supabase
          .from("products")
          .insert([productData])
          .select();

        error = insertError;

        if (insertedProduct && insertedProduct[0]) {
          console.log("Successfully created product:", insertedProduct[0].id);
          
          // Track product_listed event in PostHog
          // Use posthog if available, otherwise trackEvent will use global instance
          try {
            trackEvent("product_listed", {
              item_id: insertedProduct[0].id,
              item_name: productData.title,
              category: productData.category_main || productData.category_subcategory || "unknown",
              price: productData.price,
              vendor_id: vendor.id,
              condition: productData.condition,
            }, posthog || undefined);
            console.log("✅ [POSTHOG] product_listed event tracked");
          } catch (analyticsError) {
            console.warn("⚠️ [POSTHOG] Failed to track product_listed:", analyticsError);
          }
        }
      }

      if (error) {
        console.error("Database error:", error);
        throw error;
      }

      // Force immediate refresh of product lists
      console.log("Invalidating queries to refresh product lists");
      await queryClient.invalidateQueries({ queryKey: ["my-products"] });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await refetch();

      // Show success toast message
      showToast(isEditing ? "Product Updated" : "Product Added");

      navigation.goBack();
    } catch (error) {
      console.error(
        `Error ${isEditing ? "updating" : "creating"} product:`,
        error
      );
      platformAlert(
        "Error",
        `${
          error.message || `Failed to ${isEditing ? "update" : "add"} product`
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) return <LoadingState />;

  return (
    <KeyboardAwareScrollView 
      style={styles.container} 
      extraHeight={100}
      enableResetScrollToCoords={false}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.welcomeSection}>
        <Text style={styles.headerTitle}>
          {isEditing ? "Edit Product" : "Add Product"}
        </Text>
      </View>

      <View style={[styles.section, { marginBottom: spacing.xs }]}>
        <Text
          style={[
            styles.sectionTitle,
            { marginHorizontal: spacing.lg, marginTop: spacing.md },
          ]}
        >
          Product Images *
        </Text>
        <Text
          style={[styles.sectionSubtitle, { marginHorizontal: spacing.lg }]}
        >
          Add at least one image of your product
        </Text>
        <ImagePickerStrip
          images={formData.images}
          vendorId={vendor?.id}
          max={5}
          enableCrop={false}
          onChange={(next) =>
            setFormData((prev) => ({ ...prev, images: next }))
          }
        />
      </View>

      <View style={[styles.form, { paddingTop: 0 }]}>
        {/* Basic Info Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Basic Information</Text>
          <View style={styles.inputWrapper}>
            <Text style={styles.characterCount}>
              {formData.title.length}/{MAX_TITLE_LENGTH}
            </Text>
            <Input
              label="Title"
              value={formData.title}
              onChangeText={(text) =>
                setFormData((prev) => ({ ...prev, title: text }))
              }
              placeholder="Enter product title"
              maxLength={MAX_TITLE_LENGTH}
              required
            />
          </View>

          <View style={styles.inputWrapper}>
            <Text style={styles.characterCount}>
              {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
            </Text>
            <Input
              label="Description"
              value={formData.description}
              onChangeText={(text) =>
                setFormData((prev) => ({ ...prev, description: text }))
              }
              multiline
              numberOfLines={4}
              style={styles.textArea}
              placeholder="Describe your product in detail"
              maxLength={MAX_DESCRIPTION_LENGTH}
              required
            />
          </View>
        </View>

        {/* Pricing & Stock Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Pricing & Stock</Text>

          <Input
            label="Price"
            value={formatPriceForDisplay(formData.price)}
            onChangeText={handlePriceChange}
            onFocus={() => setPriceFocused(true)}
            onBlur={() => setPriceFocused(false)}
            keyboardType="decimal-pad"
            placeholder="$0.00"
            maxLength={MAX_PRICE_LENGTH}
            required
          />

          <Input
            label="Quantity Available"
            value={formData.quantity}
            onChangeText={(text) =>
              setFormData((prev) => ({ ...prev, quantity: text }))
            }
            keyboardType="number-pad"
            placeholder="Enter quantity available"
            maxLength={MAX_QUANTITY_LENGTH}
            required
          />
        </View>

        {/* Product Details Section */}
        <View style={[styles.section, { marginTop: spacing.lg }]}>
          <Text style={styles.sectionTitle}>Product Details</Text>

          <Select
            label="Category"
            value={formData.category_main}
            onValueChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                category_main: value,
                category_subcategory: "",
                category_item: "",
              }))
            }
            options={mainCategories}
            placeholder="Select Category"
            required
          />

          {formData.category_main && (
            <Select
              label="Subcategory"
              value={formData.category_subcategory}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  category_subcategory: value,
                  category_item: "",
                }))
              }
              options={availableSubcategories}
              placeholder="Select Subcategory"
              required
            />
          )}

          {formData.category_main && formData.category_subcategory && (
            <Select
              label="Item Type"
              value={formData.category_item}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, category_item: value }))
              }
              options={availableItems}
              placeholder="Select Item Type"
              required
            />
          )}

          <Select
            label="Condition"
            value={formData.condition}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, condition: value }))
            }
            options={[
              { label: "New", value: "new" },
              { label: "Like New", value: "like_new" },
              { label: "Good", value: "good" },
              { label: "Fair", value: "fair" },
            ]}
            placeholder="Select Condition"
            required
          />

          <View style={styles.inputWrapper}>
            <Text style={styles.characterCount}>
              {formData.size.join(", ").length}/{MAX_SIZE_LENGTH}
            </Text>
            <Input
              label="Size (optional)"
              value={formData.size.join(", ")}
              onChangeText={(text) =>
                setFormData((prev) => ({
                  ...prev,
                  size: text.split(",").map((s) => s.trim()),
                }))
              }
              placeholder="Enter sizes (e.g., 'M, L, XL')"
              maxLength={MAX_SIZE_LENGTH}
            />
          </View>

          <View style={styles.inputWrapper}>
            <Text style={styles.characterCount}>
              {formData.brand.length}/{MAX_BRAND_LENGTH}
            </Text>
            <Input
              label="Brand"
              value={formData.brand}
              onChangeText={(text) =>
                setFormData((prev) => ({ ...prev, brand: text }))
              }
              placeholder="Enter brand name"
              maxLength={MAX_BRAND_LENGTH}
              required
            />
          </View>

          <View style={styles.inputWrapper}>
            <Text style={styles.characterCount}>
              {formData.color.join(", ").length}/{MAX_COLOR_LENGTH}
            </Text>
            <Input
              label="Color (optional)"
              value={formData.color.join(", ")}
              onChangeText={(text) =>
                setFormData((prev) => ({
                  ...prev,
                  color: text.split(",").map((c) => c.trim()),
                }))
              }
              placeholder="Enter colors (e.g., 'Black, Blue')"
              maxLength={MAX_COLOR_LENGTH}
            />
          </View>
        </View>

        <Button 
          onPress={handleSubmit} 
          loading={loading}
          style={{ height: 52, borderRadius: 26, marginTop: spacing.lg }}
          textStyle={{ fontSize: 16, fontWeight: "700" }}
        >
          {isEditing ? "Update Product" : "Add Product"}
        </Button>
      </View>
      
      <StripeVerificationModal
        visible={showStripeModal}
        onClose={() => setShowStripeModal(false)}
        stripeStatus={stripeStatus}
        title="Payment Setup Required"
        description="You need to complete your Stripe Express setup to add and sell products. This ensures secure payment processing for your customers."
      />
    </KeyboardAwareScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    form: {
      padding: spacing.lg,
    },
    section: {
      marginBottom: spacing.sm,
      gap: 18,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 4,
      letterSpacing: -0.3,
    },
    inputWrapper: {
      position: "relative",
      marginTop: 0,
      marginBottom: 0,
    },
    characterCount: {
      position: "absolute",
      top: 0,
      right: 0,
      fontSize: 11,
      fontWeight: "600",
      color: colors.textLight,
      zIndex: 1,
    },
    textArea: {
      minHeight: 120, // Slightly taller
      textAlignVertical: "top",
      paddingTop: 12,
    },
    sectionSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
      fontWeight: "500",
    },
    warningText: {
      ...typography.body,
      fontSize: 14,
      color: colors.error || "#F43F5E", // Rose Red fallback
      marginTop: spacing.sm,
      fontWeight: "500",
    },
  });
